// src/views/DashboardView.js
import React, { useState, useEffect, useMemo } from 'react';
import { restClient } from '@massive.com/client-js';
import { ComposedChart, LineChart, Line, Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { 
  TrendingUp, Loader2, Plus, Trash2, Wallet, 
  X, Search, Globe, Info, ExternalLink, 
  ChevronUp, ChevronDown, PieChart as PieIcon, AlertCircle,
  Calculator, FileText, Upload,
  Bell, ChevronRight, Activity, CheckSquare, Square, BarChart3, Eye, Target, ShieldAlert
} from 'lucide-react';
import { API_BASE } from '../api/db';
import { RiskBadge, MetricRow } from '../components/SharedUI';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#3b82f6', '#ec4899'];

const MARKET_INDICES = [
  { ticker: "SPY", name: "S&P 500" }, { ticker: "QQQ", name: "NASDAQ" },
  { ticker: "DIA", name: "DOW JONES" }, { ticker: "IWM", name: "RUSSELL 2000" },
  { ticker: "USFR", name: "B500" }, { ticker: "IEF", name: "US 10 YR" }
];

const KNOWN_BOND_ETFS = ['VTIP', 'SHY', 'IEF', 'TLT', 'LQD', 'HYG', 'BND', 'MUB', 'BNDX', 'AGG', 'GOVT'];

const ALL_METRICS = [
  { id: 'Price', type: 'overlay', color: '#10b981', desc: 'Daily closing price.' },
  { id: 'SMA 20', type: 'overlay', color: '#f59e0b', desc: 'Short-term trend. Price crossing above is bullish.' },
  { id: 'SMA 50', type: 'overlay', color: '#6366f1', desc: 'Medium-term institutional trend line.' },
  { id: 'SMA 200', type: 'overlay', color: '#ec4899', desc: 'Long-term trend. The ultimate bull/bear separator.' },
  { id: 'Bollinger Bands', type: 'overlay', color: '#3b82f6', desc: 'Measures volatility. Hitting the top band suggests overbought; hitting bottom suggests oversold.' },
  { id: 'Volume', type: 'oscillator', color: '#94a3b8', desc: 'Number of shares traded. Spikes indicate high interest or institutional buying.' },
  { id: 'RSI', type: 'oscillator', color: '#ec4899', desc: 'Momentum oscillator. >70 is Overbought (risk of drop). <30 is Oversold (due for bounce).' },
  { id: 'MACD', type: 'oscillator', color: '#8b5cf6', desc: 'Trend-following momentum. Crossing above zero is a bullish signal.' }
];

const TIMEFRAMES = ['1D', '7D', '30D', '3M', '6M', '1Y', '2Y', '5Y'];

const FUNDAMENTAL_TOOLTIPS = {
  "Revenue (TTM)": "Total sales over the trailing 12 months.",
  "Rev Growth (YoY)": "Percentage increase or decrease in revenue compared to the same quarter last year.",
  "Gross Margin": "Percentage of revenue left after subtracting the cost of goods sold.",
  "Net Income": "Total profit after all expenses, taxes, and costs.",
  "Diluted EPS": "Earnings Per Share. Net income divided by total outstanding shares.",
  "EBITDA (TTM)": "Earnings Before Interest, Taxes, Depreciation, and Amortization. A proxy for operating cash flow.",
  "EV / EBITDA": "Enterprise Value to EBITDA. A valuation metric used to compare companies ignoring capital structure. Lower often means cheaper.",
  "Free Cash Flow": "Cash remaining after operating expenses and capital expenditures. Vital for dividend safety.",
  "Total Cash": "Total cash and short-term investments on the balance sheet.",
  "Total Debt": "Total short-term and long-term debt.",
  "Debt / Equity": "A measure of financial leverage. >2.0 can indicate high risk.",
  "Current Ratio": "Current Assets divided by Current Liabilities. >1.0 means the company can comfortably pay bills.",
  "Book Value / Share": "The theoretical liquidation value of the company divided by outstanding shares.",
  "Price / Book": "Compares market value to book value. <1.0 can indicate an undervalued asset.",
  "Return on Equity": "Net Income divided by Shareholder's Equity. Measures profitability efficiency.",
  "Operating Margin": "Profit generated from core operations before interest and taxes.",
  "Total Assets": "The total value of everything the ETF owns.",
  "NAV Price": "Net Asset Value. The total value of the ETF's assets divided by its outstanding shares.",
  "YTD Return": "Year-To-Date return since January 1st.",
  "3-Year Return": "Average annual return over the last 3 years.",
  "5-Year Return": "Average annual return over the last 5 years.",
  "Beta": "Volatility relative to the market. 1.0 is market average. <1.0 is lower risk."
};

export default function DashboardView({ setCurrentRoute, portfolio, setPortfolio, watchlist, setWatchlist, marketData, setMarketData, indexData, setIndexData, notes, setNotes, globalSearchTicker, setGlobalSearchTicker, setSimTicker, themeClasses }) {
  const [lookupTicker, setLookupTicker] = useState("");
  const [quickData, setQuickData] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [expandedHoldings, setExpandedHoldings] = useState([]);
  
  const [watchData, setWatchData] = useState({});
  const [expandedWatch, setExpandedWatch] = useState([]);
  const [watchInput, setWatchInput] = useState('');

  const [indexHistoryData, setIndexHistoryData] = useState({});
  const [showDeepDive, setShowDeepDive] = useState(false);
  const [diveLoading, setDiveLoading] = useState(false);
  const [diveError, setDiveError] = useState(null);
  const [history, setHistory] = useState([]);
  
  const [diveTimeframe, setDiveTimeframe] = useState('1Y');
  const [selectedMetrics, setSelectedMetrics] = useState(['Price']);
  
  const [fullDetails, setFullDetails] = useState(null);
  const [tradeForm, setTradeForm] = useState({ shares: 1, costBasis: '', fees: 0, category: 'General', type: 'Stock', interestRate: '', maturityDate: '' });

  // Prospectus Upload State
  const [isUploadingProspectus, setIsUploadingProspectus] = useState(false);
  const [prospectusSummary, setProspectusSummary] = useState(null);
  const [showProspectusModal, setShowProspectusModal] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (globalSearchTicker) {
      setLookupTicker(globalSearchTicker);
      executeSearch(globalSearchTicker);
      setGlobalSearchTicker("");
    }
  }, [globalSearchTicker, setGlobalSearchTicker]); 

  const calculateRiskMetrics = async (ticker) => {
    try {
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setFullYear(toDate.getFullYear() - 1); 
      
      const toStr = toDate.toISOString().split('T')[0];
      const fromStr = fromDate.toISOString().split('T')[0];

      const baseUrl = `${window.location.origin}${API_BASE}`;
      const [stockRes, spyRes] = await Promise.all([
        fetch(`${baseUrl}/massive/v2/aggs/ticker/${ticker}/range/1/day/${fromStr}/${toStr}?adjusted=true&sort=asc&limit=300`),
        fetch(`${baseUrl}/massive/v2/aggs/ticker/SPY/range/1/day/${fromStr}/${toStr}?adjusted=true&sort=asc&limit=300`)
      ]);

      const stockData = await stockRes.json();
      const spyData = await spyRes.json();

      const history = stockData.results ? stockData.results.map(r => ({ date: r.t, close: r.c })) : [];

      if (!stockData.results || !spyData.results) return { beta: 1.0, sharpe: 0.0, sortino: 0.0, history, sma50: null };

      const spyMap = {};
      spyData.results.forEach(d => { spyMap[d.t] = d.c; });

      const stockReturns = [];
      const spyReturns = [];
      const closes = stockData.results.map(r => r.c);

      for (let i = 1; i < stockData.results.length; i++) {
        const today = stockData.results[i];
        const yesterday = stockData.results[i - 1];
        if (spyMap[today.t] && spyMap[yesterday.t]) {
          stockReturns.push((today.c - yesterday.c) / yesterday.c);
          spyReturns.push((spyMap[today.t] - spyMap[yesterday.t]) / spyMap[yesterday.t]);
        }
      }

      const meanStock = stockReturns.reduce((a, b) => a + b, 0) / stockReturns.length || 0;
      const meanSpy = spyReturns.reduce((a, b) => a + b, 0) / spyReturns.length || 0;

      let covariance = 0, varianceSpy = 0, varianceStock = 0;
      for (let i = 0; i < stockReturns.length; i++) {
        covariance += (stockReturns[i] - meanStock) * (spyReturns[i] - meanSpy);
        varianceSpy += Math.pow((spyReturns[i] - meanSpy), 2);
        varianceStock += Math.pow((stockReturns[i] - meanStock), 2);
      }
      
      const beta = varianceSpy === 0 ? 1.0 : (covariance / varianceSpy);
      const dailyRf = 0.045 / 252;
      const excessReturns = stockReturns.map(r => r - dailyRf);
      const meanExcess = excessReturns.reduce((a, b) => a + b, 0) / excessReturns.length || 0;
      
      const stdDevStock = Math.sqrt(varianceStock / stockReturns.length) || 0;
      const sharpe = stdDevStock === 0 ? 0 : (meanExcess / stdDevStock) * Math.sqrt(252);

      const downsideReturns = excessReturns.filter(r => r < 0);
      let downsideVariance = 0;
      if (downsideReturns.length > 0) {
          const sumSquaredDownside = downsideReturns.reduce((sum, r) => sum + (r * r), 0);
          downsideVariance = sumSquaredDownside / stockReturns.length; 
      }
      const downsideDeviation = Math.sqrt(downsideVariance) || 0;
      const sortino = downsideDeviation === 0 ? 0 : (meanExcess / downsideDeviation) * Math.sqrt(252);

      const sma50 = closes.length >= 50 ? closes.slice(-50).reduce((a,b)=>a+b,0)/50 : null;

      return { beta, sharpe, sortino, history, sma50 };
    } catch (e) {
      console.warn("Risk Metrics Calculation failed");
      return { beta: 1.0, sharpe: 0.0, sortino: 0.0, history: [], sma50: null };
    }
  };

  useEffect(() => {
    const sync = async () => {
      const pResults = {};
      const iResults = {};
      
      const allTickers = [...new Set([...portfolio.map(p => p.ticker), ...MARKET_INDICES.map(idx => idx.ticker)])];
      if (allTickers.length === 0) return;

      const baseUrl = `${window.location.origin}${API_BASE}`;
      const tickersString = allTickers.join(',');
      
      let snapMap = {};
      try {
         const snapRes = await fetch(`${baseUrl}/massive/v2/snapshot/locale/us/markets/stocks/tickers?tickers=${tickersString}`).then(r => r.json());
         if (snapRes && snapRes.tickers) {
             snapRes.tickers.forEach(t => { snapMap[t.ticker] = t; });
         }
      } catch(e) { console.warn("Portfolio snapshot bulk fetch failed"); }

      await Promise.all(portfolio.map(async (p) => {
        try {
          const t = snapMap[p.ticker] || {};
          
          let histData = [];
          try {
             const metrics = await calculateRiskMetrics(p.ticker);
             histData = metrics.history || [];
          } catch(e) {}

          pResults[p.ticker] = { 
             price: t.day?.c || t.lastTrade?.p || t.prevDay?.c || 0, 
             change: t.todaysChangePerc?.toFixed(2) || "0.00", 
             low: t.day?.l || '---', 
             high: t.day?.h || '---',
             history: histData
          };
        } catch (e) { pResults[p.ticker] = { price: 0, change: "0.00", low: '---', high: '---', history: [] }; }
      }));
      setMarketData(pResults);

      MARKET_INDICES.forEach(idx => {
          const t = snapMap[idx.ticker] || {};
          iResults[idx.name] = t.day?.c || t.lastTrade?.p || t.prevDay?.c || 0;
      });
      setIndexData(iResults);
    };
    sync();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolio, setIndexData, setMarketData]);

  useEffect(() => {
    const syncWatchlist = async () => {
        const newWatchData = { ...watchData };
        let updated = false;

        const tickersToFetch = watchlist.filter(t => !newWatchData[t]);
        if (tickersToFetch.length === 0) return;

        const baseUrl = `${window.location.origin}${API_BASE}`;
        const tickersString = tickersToFetch.join(',');

        let snapMap = {};
        try {
           const snapRes = await fetch(`${baseUrl}/massive/v2/snapshot/locale/us/markets/stocks/tickers?tickers=${tickersString}`).then(r => r.json());
           if (snapRes && snapRes.tickers) {
               snapRes.tickers.forEach(t => { snapMap[t.ticker] = t; });
           }
        } catch(e) { console.warn("Watchlist snapshot bulk fetch failed"); }

        await Promise.all(tickersToFetch.map(async (ticker) => {
            try {
                const t = snapMap[ticker] || {};
                const metrics = await calculateRiskMetrics(ticker);

                newWatchData[ticker] = {
                    price: t.day?.c || t.lastTrade?.p || t.prevDay?.c || 0,
                    change: t.todaysChangePerc || 0,
                    range: `${t.day?.l?.toFixed(2) || '---'} - ${t.day?.h?.toFixed(2) || '---'}`,
                    beta: metrics.beta.toFixed(2),
                    sharpe: metrics.sharpe.toFixed(2),
                    sortino: metrics.sortino.toFixed(2),
                    history: metrics.history || []
                };
                updated = true;
            } catch(e) {}
        }));
        if (updated) setWatchData(newWatchData);
    };
    syncWatchlist();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlist]);

  useEffect(() => {
    const syncIndices = async () => {
      const newIndexData = {};
      const baseUrl = `${window.location.origin}${API_BASE}`;
      const tickersString = MARKET_INDICES.map(idx => idx.ticker).join(',');

      let snapMap = {};
      try {
         const snapRes = await fetch(`${baseUrl}/massive/v2/snapshot/locale/us/markets/stocks/tickers?tickers=${tickersString}`).then(r => r.json());
         if (snapRes && snapRes.tickers) {
             snapRes.tickers.forEach(t => { snapMap[t.ticker] = t; });
         }
      } catch(e) { console.warn("Indices snapshot bulk fetch failed"); }

      await Promise.all(MARKET_INDICES.map(async (idx) => {
        try {
          const t = snapMap[idx.ticker] || {};
          const metrics = await calculateRiskMetrics(idx.ticker);
          
          newIndexData[idx.ticker] = {
              name: idx.name,
              price: t.day?.c || t.lastTrade?.p || t.prevDay?.c || 0,
              change: t.todaysChangePerc || 0,
              history: metrics.history || []
          };
        } catch(e) {}
      }));
      setIndexHistoryData(newIndexData);
    };
    syncIndices();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addWatchlistItem = (e) => {
      e.preventDefault();
      if (!watchInput) return;
      handleAddToWatchlist(watchInput);
      setWatchInput("");
  };

  const handleAddToWatchlist = (ticker) => {
    if (!ticker) return;
    const t = ticker.toUpperCase().trim();
    if (!watchlist.includes(t)) {
        setWatchlist([...watchlist, t]);
    }
  };

  const { totalValue, totalPL, portfolioData, allocationData } = useMemo(() => {
    let currentVal = 0, totalC = 0;
    const items = portfolio.map(item => {
      const pData = marketData[item.ticker] || { price: 0, history: [] };
      const val = pData.price * item.shares;
      const rawCost = item.costBasis * item.shares;
      const totalInvestment = rawCost + (Number(item.fees) || 0);
      const adjCostBasis = item.shares > 0 ? totalInvestment / item.shares : 0;
      const pl = val - totalInvestment;
      const plPercent = totalInvestment > 0 ? ((val - totalInvestment) / totalInvestment) * 100 : 0;
      
      let annualIncome = 0;
      if (item.divYield && item.divYield !== "N/A" && item.divYield !== "---" && item.divYield !== "Fetching...") {
         const yieldDec = parseFloat(item.divYield.replace('%', '')) / 100;
         annualIncome = item.shares * pData.price * yieldDec;
      }

      currentVal += val;
      totalC += totalInvestment;

      let tech = null;
      if (pData.history && pData.history.length > 0) {
          const closes = pData.history.map(h => h.close);
          const currentClose = closes[closes.length - 1];
          const close14 = closes[closes.length - 15] || closes[0];
          const return14d = close14 > 0 ? (currentClose - close14) / close14 : 0;

          const sma50 = closes.length >= 50 ? closes.slice(-50).reduce((a,b)=>a+b,0)/50 : null;
          const sma200 = closes.length >= 200 ? closes.slice(-200).reduce((a,b)=>a+b,0)/200 : null;

          let ema10 = closes[0];
          if (closes.length >= 10) {
             const k = 2 / 11;
             for(let i=1; i<closes.length; i++) {
                ema10 = (closes[i] - ema10) * k + ema10;
             }
          } else { ema10 = null; }

          tech = { return14d, sma50, sma200, ema10 };
      }

      let alerts = [];
      if (plPercent >= 300) alerts.push({ type: 'profit', msg: 'Up 300%+ from initial entry price.' });
      if (tech && tech.return14d >= 1.0) alerts.push({ type: 'profit', msg: 'Parabolic move: Up 100%+ in 14 days.' });
      if (tech && tech.sma50 && tech.sma200 && tech.sma50 < tech.sma200) alerts.push({ type: 'loss', msg: 'Death Cross activated (50 SMA < 200 SMA).' });
      if (tech && tech.sma200 && pData.price < tech.sma200) alerts.push({ type: 'loss', msg: 'Trend breakdown: Price closed below 200-day SMA.' });
      if (tech && tech.sma50 && pData.price < tech.sma50) alerts.push({ type: 'loss', msg: 'Trend breakdown: Price closed below 50-day SMA.' });
      if (tech && tech.ema10 && pData.price < tech.ema10) alerts.push({ type: 'loss', msg: 'Momentum loss: Price closed below 10-day EMA.' });

      return { ...item, currentPrice: pData.price, totalValue: val, pl, plPercent, adjCostBasis, annualIncome, alerts };
    });
    
    const allocation = items.map(item => ({ name: item.ticker, value: item.totalValue }));
    return { totalValue: currentVal, totalPL: currentVal - totalC, portfolioData: items, allocationData: allocation };
  }, [portfolio, marketData]);

  const executeSearch = async (t) => {
    if (!t) return;
    setIsSearching(true);
    setLookupTicker(t);
    setProspectusSummary(null);
    setShowProspectusModal(false);
    try {
      const tickerStr = t.toUpperCase().trim();
      const baseUrl = `${window.location.origin}${API_BASE}`;
      
      const snapRes = await fetch(`${baseUrl}/massive/v2/snapshot/locale/us/markets/stocks/tickers/${tickerStr}`).then(r => r.json());
      if (!snapRes || !snapRes.ticker) throw new Error("Ticker not found");
      
      const s = snapRes.ticker;
      let companyName = tickerStr;
      const isBondFund = KNOWN_BOND_ETFS.includes(tickerStr);
      const currentPrice = s.lastTrade?.p || s.day?.c || s.prevDay?.c || 0;
      
      setQuickData({
        ticker: tickerStr,
        name: companyName, 
        assetClass: isBondFund ? 'Fixed Income' : 'Equity',
        price: currentPrice,
        change: s.todaysChangePerc?.toFixed(2) || "0.00",
        prevClose: s.prevDay?.c || "---",
        open: s.day?.o || "---",
        bid: s.lastQuote?.b || "---",
        ask: s.lastQuote?.a || "---",
        dayRange: `${s.day?.l || '---'} - ${s.day?.h || '---'}`,
        fiftyTwoWeekHigh: "Fetching...",
        fiftyTwoWeekLow: "Fetching...",
        marketCap: "Fetching...", peRatio: "Fetching...", divYield: "Fetching...", beta: "Fetching...", sharpe: "Fetching...", sortino: "Fetching...",
        secYield: "Fetching...", duration: "Fetching...", creditRating: "Fetching...", expenseRatio: "Fetching...",
        stopLoss: 0, takeProfit: 0, riskPoints: 0
      });

      fetch(`${baseUrl}/company/${tickerStr}`).then(r=>r.json()).then(data => {
         setFullDetails({ 
            description: data.description || "No corporate description available.",
            fundamentals: data.fundamentals || null 
         });
         
         setQuickData(prev => prev ? {
             ...prev,
             marketCap: data.marketCap || "N/A",
             fiftyTwoWeekHigh: data.fundamentals ? data.fundamentals['52-Week High'] : "N/A",
             fiftyTwoWeekLow: data.fundamentals ? data.fundamentals['52-Week Low'] : "N/A",
             peRatio: data.pe || "N/A",
             divYield: data.divYield || "N/A",
             secYield: data.secYield || "N/A",
             duration: data.duration || "N/A",
             creditRating: data.creditRating || "N/A",
             expenseRatio: data.expenseRatio || "N/A"
         } : null);
      }).catch(e => console.warn(e));

      try {
        const metaRes = await fetch(`${baseUrl}/massive/v3/reference/tickers/${tickerStr}`);
        const metaData = await metaRes.json();
        if (metaData && metaData.results) {
            companyName = metaData.results.name || tickerStr;
            setQuickData(prev => prev ? { ...prev, name: companyName } : null);
        }
      } catch (e) {}

      const metrics = await calculateRiskMetrics(tickerStr);
      
      let calculatedStop = 0;
      let calculatedTarget = 0;
      let calculatedRisk = 0;

      if (currentPrice > 0) {
          if (metrics.sma50 && currentPrice > metrics.sma50) {
              calculatedStop = metrics.sma50 * 0.98;
          } else {
              calculatedStop = currentPrice * 0.92;
          }
          calculatedRisk = currentPrice - calculatedStop;
          calculatedTarget = currentPrice + (calculatedRisk * 2);
      }

      setQuickData(prev => prev ? { 
        ...prev, 
        beta: metrics.beta.toFixed(2), 
        sharpe: metrics.sharpe.toFixed(2),
        sortino: metrics.sortino.toFixed(2),
        stopLoss: calculatedStop,
        takeProfit: calculatedTarget,
        riskPoints: calculatedRisk
      } : null);

      setTradeForm({...tradeForm, type: isBondFund ? 'ETF' : 'Stock', costBasis: currentPrice });
      
    } catch (e) { alert(e.message); } finally { setIsSearching(false); }
  };

  const handleLookup = (e) => {
    e.preventDefault();
    executeSearch(lookupTicker);
  };

  const commitTrade = () => {
    if(!quickData) return;
    const newTrade = { 
      ticker: quickData.ticker, name: quickData.name, 
      shares: Number(tradeForm.shares), costBasis: Number(tradeForm.costBasis), 
      fees: Number(tradeForm.fees), category: tradeForm.category, 
      type: tradeForm.type, riskScore: quickData.beta, sharpeRatio: quickData.sharpe, sortinoRatio: quickData.sortino,
      divYield: quickData.assetClass === 'Fixed Income' ? quickData.secYield : quickData.divYield,
      ...(tradeForm.type === 'Bond' && { interestRate: tradeForm.interestRate, maturityDate: tradeForm.maturityDate })
    };
    setPortfolio(prev => [...prev, newTrade]);
  };

  const fetchDeepDiveData = async (ticker, range) => {
    setDiveLoading(true);
    setDiveError(null);
    try {
      const baseUrl = `${window.location.origin}${API_BASE}`;
      const res = await fetch(`${baseUrl}/model/indicators`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ticker: ticker, range: range })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setHistory(data.indicators);
    } catch (e) { 
      setDiveError(e.message); 
    } finally { 
      setDiveLoading(false); 
    }
  };

  const triggerDeepDive = (dataOverride = quickData) => {
    if (!dataOverride) return;
    setShowDeepDive(true);
    fetchDeepDiveData(dataOverride.ticker, diveTimeframe);
  };

  const handleTimeframeChange = (tf) => {
    setDiveTimeframe(tf);
    fetchDeepDiveData(quickData.ticker, tf);
  };

  const toggleMetric = (metricId) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metricId)) return prev.filter(m => m !== metricId);
      if (prev.length >= 4) return prev; 
      return [...prev, metricId];
    });
  };

  const handleProspectusUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploadingProspectus(true);
    setProspectusSummary(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const baseUrl = `${window.location.origin}${API_BASE}`;
      const res = await fetch(`${baseUrl}/model/prospectus`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setProspectusSummary(data.summary);
      setShowProspectusModal(true);
    } catch (err) {
      alert("Error analyzing document: " + err.message);
    } finally {
      setIsUploadingProspectus(false);
      e.target.value = null; 
    }
  };

  const formatMarkdownText = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, idx) => {
      if (line.trim().startsWith('### ')) {
        return <h4 key={idx} className="font-black text-white text-sm sm:text-base mt-4 mb-2 uppercase tracking-widest">{line.replace('### ', '')}</h4>;
      }
      const isBullet = line.trim().startsWith('* ') || line.trim().startsWith('- ');
      const content = isBullet ? line.trim().substring(2) : line;
      const parts = content.split(/(\*\*.*?\*\*)/g);
      const elements = parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} className={`font-black ${themeClasses.textHead}`}>{part.slice(2, -2)}</strong>;
        return <span key={i}>{part}</span>;
      });
      if (isBullet) return <li key={idx} className="ml-4 list-disc mb-2">{elements}</li>;
      if (line.trim() === '') return <br key={idx} />;
      return <p key={idx} className="mb-3">{elements}</p>;
    });
  };

  const renderValueOrLink = (val, ticker) => {
    if (val === "Check Prospectus") {
      return (
        <a 
          href={`https://www.google.com/search?q=${ticker}+ETF+prospectus`} 
          target="_blank" 
          rel="noreferrer" 
          className="text-indigo-500 hover:text-indigo-400 hover:underline flex items-center justify-end gap-1 text-[10px]"
        >
          Search <ExternalLink size={10}/>
        </a>
      );
    }
    return val;
  };

  const activeOverlays = ALL_METRICS.filter(m => m.type === 'overlay' && selectedMetrics.includes(m.id));
  const activeOscillators = ALL_METRICS.filter(m => m.type === 'oscillator' && selectedMetrics.includes(m.id));

  return (
    <div className="grid grid-cols-12 gap-4 lg:gap-6 h-full max-w-[1900px] mx-auto overflow-x-hidden">
      <div className={`col-span-12 lg:col-span-4 flex flex-col ${themeClasses.bgCard} border ${themeClasses.border} rounded-3xl p-4 lg:p-6 shadow-xl`}>
        <h3 className="text-[10px] font-black text-indigo-500 uppercase mb-4 tracking-widest flex items-center gap-2 shrink-0"><Wallet size={14}/> Active Holdings</h3>
        <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar pr-2 pt-3 pb-2">
          {portfolioData.length === 0 ? (
             <div className={`flex items-center justify-center h-full text-[10px] font-black tracking-widest uppercase ${themeClasses.textMuted}`}>Portfolio Empty</div>
          ) : portfolioData.map((p, i) => {
            const isExpanded = expandedHoldings.includes(p.ticker);
            const activeAlerts = p.alerts || [];
            const isAlertDown = activeAlerts.some(a => a.type === 'loss');
            const isAlertUp = activeAlerts.some(a => a.type === 'profit');
            
            const isMonthly = p.type === 'Bond' || p.type === 'ETF';
            const distCount = isMonthly ? 12 : 4;
            const distLabel = isMonthly ? 'Monthly' : 'Quarterly';
            
            return (
            <div key={i} className={`${themeClasses.input} border ${isAlertDown ? 'border-red-500/50' : isAlertUp ? 'border-emerald-500/50' : themeClasses.border} p-4 rounded-2xl relative transition-all`}>
              
              {(activeAlerts.length > 0) && (
                <div className="absolute -top-3 right-4 z-10 group">
                  <span className={`cursor-help ${themeClasses.bgCard} border ${isAlertUp ? 'border-emerald-500/50 text-emerald-500' : 'border-red-500/50 text-red-500'} px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-1 animate-pulse`}>
                    <Bell size={10}/> {isAlertUp && !isAlertDown ? 'Take Profit' : isAlertDown && !isAlertUp ? 'Exit Signal' : 'Action Required'}
                  </span>
                  
                  <div className="absolute top-full right-0 mt-2 w-56 p-3 bg-black/95 border border-slate-700 rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 flex flex-col gap-1.5">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 border-b border-slate-700 pb-1">Signal Reasoning</span>
                    {activeAlerts.map((a, idx) => (
                       <div key={idx} className="flex items-start gap-2">
                         <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${a.type === 'profit' ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                         <span className="text-[10px] text-white leading-tight font-medium">{a.msg}</span>
                       </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-between items-center w-full">
                 <div className="flex items-center gap-3 w-1/3">
                   <button onClick={() => setExpandedHoldings(prev => prev.includes(p.ticker) ? prev.filter(t=>t!==p.ticker) : [...prev, p.ticker])} className={`${themeClasses.textMuted} hover:text-indigo-500 transition-colors`}>
                      {isExpanded ? <ChevronUp size={16}/> : <ChevronRight size={16}/>}
                   </button>
                   <div>
                     <span className={`font-black ${themeClasses.textHead} text-sm block cursor-pointer hover:text-indigo-500`} onClick={() => executeSearch(p.ticker)}>{p.ticker}</span>
                     {p.type && <span className={`text-[8px] font-bold ${themeClasses.textMuted} uppercase tracking-widest bg-slate-500/10 px-1 py-0.5 rounded mt-0.5 hidden sm:inline-block`}>{p.type}</span>}
                   </div>
                 </div>
                 
                 {!isExpanded && (
                   <div className="flex justify-between items-center flex-1 text-xs font-mono ml-2">
                      <span className="text-emerald-500">${p.currentPrice?.toFixed(2) || '---'}</span>
                      <span className={p.plPercent >= 0 ? 'text-emerald-500' : 'text-red-500'}>{p.plPercent > 0 ? '+' : ''}{p.plPercent.toFixed(2)}%</span>
                   </div>
                 )}
                 
                 {isExpanded && (
                   <div className="flex items-center justify-end gap-3 flex-1">
                     <RiskBadge score={p.riskScore} />
                     <button onClick={() => setPortfolio(portfolio.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 transition-colors"><Trash2 size={14}/></button>
                   </div>
                 )}
              </div>

              {isExpanded && (
                <div className={`mt-4 pt-4 border-t ${themeClasses.border}`}>
                  <div className="grid grid-cols-2 gap-y-2.5 text-[10px] font-bold">
                    <span className={`${themeClasses.textMuted} uppercase`}>Live Price</span>
                    <span className="text-right text-emerald-500 font-mono text-xs">${marketData[p.ticker]?.price || '---'}</span>
                    
                    <span className={`${themeClasses.textMuted} uppercase flex items-center`}>Shares</span>
                    <div className="flex items-center justify-end gap-2 sm:gap-3">
                      <button onClick={() => setPortfolio(prev => prev.map(item => item.ticker === p.ticker ? { ...item, shares: Math.max(0, item.shares - 1) } : item).filter(item => item.shares > 0))} className={`p-1 ${themeClasses.input} rounded hover:text-indigo-500 ${themeClasses.textMuted}`}><ChevronDown size={14}/></button>
                      <span className={`w-6 sm:w-8 text-center ${themeClasses.textHead} text-xs`}>{p.shares}</span>
                      <button onClick={() => setPortfolio(prev => prev.map(item => item.ticker === p.ticker ? { ...item, shares: item.shares + 1 } : item))} className={`p-1 ${themeClasses.input} rounded hover:text-indigo-500 ${themeClasses.textMuted}`}><ChevronUp size={14}/></button>
                    </div>

                    <span className={`${themeClasses.textMuted} uppercase self-center`}>Base Cost/Share</span>
                    <div className="flex justify-end items-center gap-0.5">
                       <span className={`${themeClasses.textMuted} text-xs`}>$</span>
                       <input type="number" value={p.costBasis} onChange={(e) => setPortfolio(prev => prev.map(item => item.ticker === p.ticker ? { ...item, costBasis: Number(e.target.value) } : item))} className={`w-14 bg-transparent text-right ${themeClasses.textHead} font-mono outline-none border-b ${themeClasses.border} focus:border-indigo-500`}/>
                    </div>

                    <span className={`${themeClasses.textMuted} uppercase self-center`}>Total Fees</span>
                    <div className="flex justify-end items-center gap-0.5">
                       <span className={`${themeClasses.textMuted} text-xs`}>$</span>
                       <input type="number" value={p.fees || 0} onChange={(e) => setPortfolio(prev => prev.map(item => item.ticker === p.ticker ? { ...item, fees: Number(e.target.value) } : item))} className={`w-14 bg-transparent text-right ${themeClasses.textHead} font-mono outline-none border-b ${themeClasses.border} focus:border-indigo-500`}/>
                    </div>

                    <span className={`${themeClasses.textMuted} uppercase mt-1`}>Adj. Cost/Share</span>
                    <span className={`text-right ${themeClasses.textHead} font-mono text-xs mt-1`}>${p.adjCostBasis.toFixed(2)}</span>

                    {p.type === 'Bond' ? (
                      <>
                        <span className="text-indigo-400 uppercase mt-1">Interest Rate</span>
                        <span className="text-right font-mono text-indigo-400 mt-1">{p.interestRate}%</span>
                        <span className="text-indigo-400 uppercase mt-1">Maturity Date</span>
                        <span className="text-right font-mono text-indigo-400 mt-1">{p.maturityDate}</span>
                      </>
                    ) : (
                      <>
                        <span className={`${themeClasses.textMuted} uppercase mt-1 hidden sm:block`}>Sharpe Ratio</span>
                        <span className={`text-right ${themeClasses.textHead} font-mono text-xs mt-1 hidden sm:block`}>{p.sharpeRatio || '---'}</span>
                        <span className={`${themeClasses.textMuted} uppercase mt-1 hidden sm:block`}>Sortino Ratio</span>
                        <span className={`text-right ${themeClasses.textHead} font-mono text-xs mt-1 hidden sm:block`}>{p.sortinoRatio || '---'}</span>
                      </>
                    )}

                    {p.annualIncome > 0 && (
                      <>
                        <span className={`col-span-2 border-t border-dashed border-emerald-500/30 my-1`}></span>
                        
                        <span className={`${themeClasses.textMuted} uppercase mt-1`}>Annual Est. Income</span>
                        <span className={`text-right text-emerald-500 font-mono text-xs mt-1`}>
                          ${p.annualIncome.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} <span className="text-[9px] text-emerald-600 hidden sm:inline">/yr</span>
                        </span>
                        
                        <span className={`${themeClasses.textMuted} uppercase mt-1 hidden sm:block`}>Est. Payout ({distLabel})</span>
                        <span className={`text-right text-emerald-500 font-mono text-xs mt-1 font-bold hidden sm:block`}>
                          ${(p.annualIncome / distCount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </span>
                      </>
                    )}

                    <span className={`col-span-2 border-t ${themeClasses.border} my-1`}></span>

                    <span className={`${themeClasses.textMuted} uppercase mt-1`}>Unrealized P/L</span>
                    <span className={`text-right font-mono text-xs mt-1 ${p.pl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {p.pl >= 0 ? '+' : ''}${p.pl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} 
                    </span>
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>

        <div className={`mt-4 pt-6 border-t ${themeClasses.border} flex justify-between items-end shrink-0`}>
          <div>
            <span className={`text-[10px] font-black uppercase ${themeClasses.textMuted} block mb-1`}>Total Equity</span>
            <span className={`text-2xl sm:text-3xl font-black ${themeClasses.textHead}`}>${totalValue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
          </div>
          <div className="text-right">
            <span className={`text-[10px] font-black uppercase ${themeClasses.textMuted} block mb-1`}>Total Return</span>
            <span className={`text-base sm:text-lg font-black font-mono block ${totalPL >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {totalPL >= 0 ? '+' : ''}${totalPL.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </span>
          </div>
        </div>

        <div className={`mt-4 pt-4 sm:mt-6 sm:pt-6 border-t ${themeClasses.border} shrink-0`}>
          <h3 className={`text-[10px] font-black ${themeClasses.textMuted} uppercase tracking-widest flex items-center gap-2 mb-2`}><PieIcon size={14} className="text-indigo-500"/> Current Allocation</h3>
          <div className="h-[120px] sm:h-[150px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={allocationData} innerRadius="60%" outerRadius="80%" paddingAngle={8} dataKey="value" label={({ name, percent }) => percent > 0.05 ? `${name}` : ''} labelLine={false} style={{ fontSize: '10px', fontWeight: 'bold', fill: '#94a3b8' }}>
                  {allocationData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      <div className={`col-span-12 lg:col-span-4 flex flex-col ${themeClasses.bgCard} border ${themeClasses.border} rounded-3xl p-4 lg:p-6 shadow-xl`}>
        <h3 className="text-[10px] font-black text-indigo-500 uppercase mb-4 tracking-widest shrink-0"><Search size={14} className="inline mr-2"/> Asset Intelligence</h3>
        <form onSubmit={handleLookup} className="relative mb-4 sm:mb-6 shrink-0">
           <input value={lookupTicker} onChange={e => setLookupTicker(e.target.value)} placeholder="SEARCH TICKER..." className={`w-full ${themeClasses.input} border ${themeClasses.border} rounded-xl py-3 sm:py-4 px-4 text-xs font-bold outline-none focus:border-indigo-500 text-indigo-500`} />
           <button type="submit" className="absolute right-4 top-3 sm:top-4 text-indigo-500">{isSearching ? <Loader2 size={18} className="animate-spin" /> : <TrendingUp size={18} />}</button>
        </form>

        {quickData ? (
          <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
            <div className="flex justify-between items-end mb-4">
              <div className="truncate pr-2">
                <h2 className={`text-xl sm:text-2xl font-black ${themeClasses.textHead} leading-tight truncate`}>{quickData.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <p className={`text-[9px] ${quickData.assetClass === 'Fixed Income' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-indigo-500/10 text-indigo-500'} font-black uppercase tracking-widest px-2 py-0.5 rounded hidden sm:block`}>
                     {quickData.assetClass}
                  </p>
                  <p className="text-xs text-indigo-500 font-bold uppercase tracking-widest">{quickData.ticker}</p>
                  <RiskBadge score={quickData.beta} />
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xl sm:text-2xl font-mono font-bold text-emerald-500">${quickData.price}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-1 mb-6 mt-4">
              <MetricRow label="Prev Close" val={quickData.prevClose} themeClasses={themeClasses} tooltip="The final price the asset traded at during the previous trading session." />
              <MetricRow label="Open" val={quickData.open} themeClasses={themeClasses} tooltip="The initial price the asset traded at when the market opened today." />
              <MetricRow label="Day Range" val={quickData.dayRange} themeClasses={themeClasses} tooltip="The highest and lowest prices the asset has traded at today." />
              
              <MetricRow label="52W High" val={quickData.fiftyTwoWeekHigh} themeClasses={themeClasses} tooltip="The highest price the asset has reached in the last 12 months." />
              <MetricRow label="52W Low" val={quickData.fiftyTwoWeekLow} themeClasses={themeClasses} tooltip="The lowest price the asset has reached in the last 12 months." />
              
              {quickData.assetClass === 'Fixed Income' ? (
                <>
                  <MetricRow label="SEC Yield (30-Day)" val={quickData.secYield} themeClasses={themeClasses} tooltip="A standardized calculation of the yield a bond fund generated over the last 30 days. The most accurate measure of current income." />
                  <MetricRow 
                    label="Effective Duration" 
                    val={renderValueOrLink(quickData.duration, quickData.ticker)} 
                    themeClasses={themeClasses} 
                    tooltip="Measures interest rate risk. If duration is 5 years, the fund's price will drop roughly 5% if interest rates rise by 1%." 
                  />
                  <MetricRow 
                    label="Avg Credit Quality" 
                    val={renderValueOrLink(quickData.creditRating, quickData.ticker)} 
                    themeClasses={themeClasses} 
                    tooltip="The average financial health of the bonds in this fund. AAA is the safest (Govt), while BB or lower is high-risk 'Junk' status." 
                  />
                  <MetricRow label="Expense Ratio" val={quickData.expenseRatio} themeClasses={themeClasses} tooltip="The annual fee the fund manager charges you to hold this ETF." />
                </>
              ) : (
                <>
                  <MetricRow label="Beta (1Y)" val={quickData.beta} themeClasses={themeClasses} tooltip="Measures volatility over 12 months. A Beta of 1 matches the market perfectly. >1 is highly volatile, <1 is defensively stable." />
                  <MetricRow label="Sharpe Ratio" val={quickData.sharpe} themeClasses={themeClasses} tooltip="Measures risk-adjusted return relative to the risk-free rate. Punishes BOTH upside and downside volatility." />
                  <MetricRow label="Sortino Ratio" val={quickData.sortino} themeClasses={themeClasses} tooltip="Measures downside risk-adjusted return. Punishes ONLY downside drops. >1 is good, >2 is excellent." />
                  <MetricRow label="Market Cap" val={quickData.marketCap} themeClasses={themeClasses} tooltip="The total dollar market value of a company's outstanding shares of stock. (Shares × Price)" />
                  <MetricRow label="P/E (TTM)" val={quickData.peRatio} themeClasses={themeClasses} tooltip="Price-to-Earnings Ratio. Shows how much investors are willing to pay per $1 of company earnings. Lower is generally 'cheaper'." />
                  <MetricRow label="Dividend Yield" val={quickData.divYield} themeClasses={themeClasses} tooltip="How much cash a company pays out to shareholders each year, relative to its stock price." />
                </>
              )}
            </div>

            <div className={`${themeClasses.input} border ${themeClasses.border} rounded-2xl p-4`}>
               <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-4">Execute Trade</h4>
               
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                 <div>
                   <label className={`text-[9px] font-bold ${themeClasses.textMuted} uppercase block mb-1`}>Asset Type</label>
                   <select value={tradeForm.type} onChange={e=>setTradeForm({...tradeForm, type: e.target.value})} className={`w-full ${themeClasses.bgCard} p-2 rounded-lg text-xs border ${themeClasses.border} outline-none ${themeClasses.textHead}`}>
                     <option>Stock</option><option>Bond</option><option>Crypto</option><option>ETF</option>
                   </select>
                 </div>
                 <div>
                   <label className={`text-[9px] font-bold ${themeClasses.textMuted} uppercase block mb-1`}>Shares/Qty</label>
                   <input type="number" value={tradeForm.shares} onChange={e=>setTradeForm({...tradeForm, shares: e.target.value})} className={`w-full ${themeClasses.bgCard} p-2 rounded-lg text-xs border ${themeClasses.border} outline-none ${themeClasses.textHead}`} />
                 </div>
                 <div>
                   <label className={`text-[9px] font-bold ${themeClasses.textMuted} uppercase block mb-1`}>Cost Basis</label>
                   <input type="number" value={tradeForm.costBasis} onChange={e=>setTradeForm({...tradeForm, costBasis: e.target.value})} className={`w-full ${themeClasses.bgCard} p-2 rounded-lg text-xs border ${themeClasses.border} outline-none ${themeClasses.textHead}`} />
                 </div>
                 <div>
                   <label className={`text-[9px] font-bold ${themeClasses.textMuted} uppercase block mb-1`}>Tx Fees ($)</label>
                   <input type="number" value={tradeForm.fees} onChange={e=>setTradeForm({...tradeForm, fees: e.target.value})} className={`w-full ${themeClasses.bgCard} p-2 rounded-lg text-xs border ${themeClasses.border} outline-none ${themeClasses.textHead}`} />
                 </div>
               </div>

               {tradeForm.type === 'Bond' && (
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 p-3 bg-indigo-500/5 rounded-xl border border-indigo-500/20">
                   <div>
                     <label className="text-[9px] font-bold text-indigo-500 uppercase block mb-1">Interest Rate (%)</label>
                     <input type="number" placeholder="5.25" value={tradeForm.interestRate} onChange={e=>setTradeForm({...tradeForm, interestRate: e.target.value})} className={`w-full ${themeClasses.bgCard} p-2 rounded-lg text-xs border ${themeClasses.border} outline-none ${themeClasses.textHead}`} />
                   </div>
                   <div>
                     <label className="text-[9px] font-bold text-indigo-500 uppercase block mb-1">Maturity Date</label>
                     <input type="date" value={tradeForm.maturityDate} onChange={e=>setTradeForm({...tradeForm, maturityDate: e.target.value})} className={`w-full ${themeClasses.bgCard} p-2 rounded-lg text-xs border ${themeClasses.border} outline-none ${themeClasses.textHead}`} />
                   </div>
                 </div>
               )}

               {quickData.assetClass !== 'Fixed Income' && quickData.stopLoss > 0 && (
                 <div className="mb-4 bg-black/20 border border-slate-700/50 rounded-xl p-3 flex flex-col gap-2">
                   <div className="flex justify-between items-center">
                     <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><ShieldAlert size={10}/> Stop Loss</span>
                     <span className="text-xs font-mono font-bold text-red-400">${quickData.stopLoss.toFixed(2)}</span>
                   </div>
                   <div className="flex justify-between items-center border-t border-slate-700/50 pt-2">
                     <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><Target size={10}/> Target (1:2 R/R)</span>
                     <span className="text-xs font-mono font-bold text-emerald-400">${quickData.takeProfit.toFixed(2)}</span>
                   </div>
                   <div className="text-[8px] text-center text-slate-500 font-medium uppercase tracking-widest mt-1">
                      Risking <strong className="text-white">${quickData.riskPoints.toFixed(2)}/share</strong>
                   </div>
                 </div>
               )}

               <button onClick={commitTrade} className="w-full bg-indigo-600 py-3 rounded-xl text-white font-black uppercase text-[10px] tracking-widest shadow-lg hover:bg-indigo-500 transition-colors mb-3">
                  Commit to Portfolio
               </button>
               
               <button 
                  onClick={() => handleAddToWatchlist(quickData.ticker)} 
                  disabled={watchlist.includes(quickData.ticker)} 
                  className={`w-full py-3 rounded-xl font-black uppercase text-[10px] tracking-widest transition-colors mb-3 flex items-center justify-center gap-2 ${watchlist.includes(quickData.ticker) ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 cursor-default' : 'bg-transparent border border-indigo-600/30 text-indigo-500 hover:bg-indigo-500/10'}`}
               >
                  <Eye size={12}/> {watchlist.includes(quickData.ticker) ? 'On Watchlist' : 'Add to Watchlist'}
               </button>

               <div className="grid grid-cols-2 gap-3">
                 <button onClick={() => triggerDeepDive()} className="w-full bg-transparent border border-indigo-600/30 text-indigo-500 py-3 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-indigo-500/10 transition-colors flex items-center justify-center gap-2">
                    <ExternalLink size={12}/> Deep Dive
                 </button>
                 <button onClick={() => { setSimTicker(quickData.ticker); setCurrentRoute('simulator'); }} className="w-full bg-transparent border border-indigo-600/30 text-indigo-500 py-3 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-indigo-500/10 transition-colors flex items-center justify-center gap-2">
                    <Calculator size={12}/> Simulator
                 </button>
               </div>
            </div>
          </div>
        ) : (
          <div className={`flex-1 flex flex-col items-center justify-center ${themeClasses.textMuted} opacity-50 min-h-[200px]`}>
             <Search size={32} sm={{size: 48}} className="mb-4" />
             <p className="text-xs uppercase tracking-widest font-black">Awaiting Query</p>
          </div>
        )}
      </div>

      <div className="col-span-12 lg:col-span-4 flex flex-col gap-6 overflow-y-auto custom-scrollbar pr-2">
        
        <div className={`${themeClasses.bgCard} border ${themeClasses.border} p-4 lg:p-6 rounded-3xl flex flex-col shrink-0 min-h-[350px] shadow-sm`}>
           <div className="flex justify-between items-center mb-4 shrink-0">
             <h3 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest flex items-center gap-2"><Eye size={14}/> Stock Watchlist</h3>
           </div>
           
           <form onSubmit={addWatchlistItem} className="relative mb-4 shrink-0">
              <input value={watchInput} onChange={e => setWatchInput(e.target.value)} placeholder="ADD TICKER..." className={`w-full ${themeClasses.input} border ${themeClasses.border} rounded-xl py-3 px-4 text-xs font-bold outline-none focus:border-indigo-500 text-indigo-500`} />
              <button type="submit" className="absolute right-3 top-3 text-indigo-500 hover:text-indigo-400"><Plus size={16} /></button>
           </form>

           <div className="flex-1 overflow-y-auto space-y-2 custom-scrollbar pr-1">
              {watchlist.map((ticker, i) => {
                const data = watchData[ticker];
                const isExpanded = expandedWatch.includes(ticker);
                const isUp = data && data.change >= 0;

                return (
                  <div key={i} className={`${themeClasses.input} border ${themeClasses.border} rounded-xl transition-all`}>
                    <div className="flex items-center justify-between p-3 w-full">
                       <div className="flex items-center gap-2 w-1/3">
                         <button onClick={() => setExpandedWatch(prev => isExpanded ? prev.filter(t=>t!==ticker) : [...prev, ticker])} className={`${themeClasses.textMuted} hover:text-indigo-500 transition-colors`}>
                            {isExpanded ? <ChevronUp size={14}/> : <ChevronRight size={14}/>}
                         </button>
                         <span className={`font-black ${themeClasses.textHead} text-xs cursor-pointer hover:text-indigo-500`} onClick={() => executeSearch(ticker)}>{ticker}</span>
                       </div>

                       {!data ? (
                          <div className="flex-1 text-center"><Loader2 size={12} className={`animate-spin ${themeClasses.textMuted}`} /></div>
                       ) : (
                          <>
                            <div className="w-1/4 text-xs font-mono font-bold text-emerald-500 text-right pr-2">
                               ${data.price.toFixed(2)}
                            </div>
                            
                            <div className="w-1/4 h-[30px] flex items-center justify-center opacity-70">
                              {data.history && data.history.length > 0 && (
                                <ResponsiveContainer width="100%" height="100%">
                                  <LineChart data={data.history}>
                                    <Line type="monotone" dataKey="close" stroke={isUp ? '#10b981' : '#ef4444'} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                                    <YAxis domain={['auto', 'auto']} hide={true} />
                                  </LineChart>
                                </ResponsiveContainer>
                              )}
                            </div>
                          </>
                       )}

                       <div className="flex items-center justify-end w-8 sm:w-12">
                          <button onClick={() => setWatchlist(watchlist.filter(t => t !== ticker))} className="text-red-500/50 hover:text-red-500 transition-colors"><Trash2 size={12}/></button>
                       </div>
                    </div>

                    {isExpanded && data && (
                      <div className={`p-3 border-t ${themeClasses.border} bg-black/10`}>
                        <div className="grid grid-cols-2 gap-y-2 text-[10px] font-bold">
                          <span className={`${themeClasses.textMuted} uppercase`}>Day Range</span>
                          <span className={`text-right ${themeClasses.textHead} font-mono text-xs truncate pl-2`}>{data.range}</span>
                          
                          <span className={`${themeClasses.textMuted} uppercase`}>Beta (1Y)</span>
                          <span className={`text-right ${themeClasses.textHead} font-mono text-xs`}><RiskBadge score={data.beta} /></span>

                          <span className={`${themeClasses.textMuted} uppercase hidden sm:block`}>Sharpe</span>
                          <span className={`text-right ${themeClasses.textHead} font-mono text-xs hidden sm:block`}>{data.sharpe}</span>

                          <span className={`${themeClasses.textMuted} uppercase hidden sm:block`}>Sortino</span>
                          <span className={`text-right ${themeClasses.textHead} font-mono text-xs hidden sm:block`}>{data.sortino}</span>
                        </div>
                        <div className="mt-3 pt-3 border-t border-slate-700/50 flex justify-end">
                          <button onClick={() => executeSearch(ticker)} className="bg-indigo-500/10 text-indigo-500 hover:bg-indigo-500 hover:text-white px-4 py-1.5 rounded uppercase tracking-widest text-[9px] font-black transition-colors">
                            Analyze Asset
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
              {watchlist.length === 0 && <p className={`text-center ${themeClasses.textMuted} text-[10px] mt-6 uppercase tracking-widest font-black`}>Watchlist is empty</p>}
           </div>
        </div>

        <div className={`${themeClasses.bgCard} border ${themeClasses.border} p-4 lg:p-6 rounded-3xl flex-1 flex flex-col shadow-xl min-h-[300px]`}>
           <div className="flex justify-between items-center mb-4 shrink-0">
             <h3 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest flex items-center gap-2"><TrendingUp size={14}/> Market Update</h3>
           </div>
           <div className="flex-1 overflow-y-auto space-y-2 custom-scrollbar pr-1">
              {MARKET_INDICES.map((idx, i) => {
                const data = indexHistoryData[idx.ticker];
                
                return (
                  <div key={i} className={`${themeClasses.input} border ${themeClasses.border} rounded-xl transition-all`}>
                    <div className="flex items-center justify-between p-3 w-full">
                       <div className="flex flex-col w-1/3">
                         <span className={`font-black ${themeClasses.textHead} text-xs cursor-pointer hover:text-indigo-500`} onClick={() => executeSearch(idx.ticker)}>{idx.ticker}</span>
                         <span className={`text-[8px] font-bold ${themeClasses.textMuted} uppercase tracking-widest truncate max-w-[100px]`}>{idx.name}</span>
                       </div>

                       {!data ? (
                          <div className="flex-1 text-center"><Loader2 size={12} className={`animate-spin ${themeClasses.textMuted}`} /></div>
                       ) : (
                          <>
                            <div className="w-1/4 h-[30px] flex items-center justify-center opacity-70">
                              {data.history && data.history.length > 0 && (
                                <ResponsiveContainer width="100%" height="100%">
                                  <LineChart data={data.history.slice(-126)}>
                                    <Line type="monotone" dataKey="close" stroke={data.change >= 0 ? '#10b981' : '#ef4444'} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                                    <YAxis domain={['auto', 'auto']} hide={true} />
                                  </LineChart>
                                </ResponsiveContainer>
                              )}
                            </div>
                            <div className="flex flex-col w-1/3 items-end pl-2">
                              <span className="text-xs font-mono font-bold text-emerald-500">${data.price.toFixed(2)}</span>
                              <span className={`text-[9px] font-mono font-bold ${data.change >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{data.change >= 0 ? '+' : ''}{data.change.toFixed(2)}%</span>
                            </div>
                          </>
                       )}
                    </div>
                  </div>
                )
              })}
           </div>
        </div>

      </div>

      {showDeepDive && (
        <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl flex items-center justify-center p-0 sm:p-6">
          <div className={`${themeClasses.bgCard} border-0 sm:border ${themeClasses.border} w-full max-w-[1400px] h-[100vh] sm:h-[90vh] rounded-none sm:rounded-[3rem] overflow-hidden flex flex-col shadow-2xl`}>
            
            <div className={`p-4 sm:p-8 border-b ${themeClasses.border} flex justify-between items-center shrink-0`}>
              <h2 className={`text-xl sm:text-2xl font-black ${themeClasses.textHead} uppercase flex items-center gap-3`}><Globe size={24} className="text-indigo-500"/> Deep Dive</h2>
              
              <div className="flex items-center gap-2 sm:gap-4">
                <button 
                   onClick={() => handleAddToWatchlist(quickData?.ticker)} 
                   disabled={watchlist.includes(quickData?.ticker)}
                   className={`px-3 sm:px-4 py-2 rounded-xl text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-colors flex items-center gap-2 ${watchlist.includes(quickData?.ticker) ? 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/50 cursor-default' : 'bg-indigo-500/10 text-indigo-500 border border-indigo-500/30 hover:bg-indigo-500 hover:text-white'}`}
                >
                  <Eye size={14}/> <span className="hidden sm:inline">{watchlist.includes(quickData?.ticker) ? 'Watching' : 'Watch Asset'}</span>
                </button>

                <button onClick={() => setShowDeepDive(false)} className={`${themeClasses.input} p-2 rounded-full hover:text-red-500 transition-colors`}><X size={20} sm={{size:24}}/></button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto lg:overflow-hidden flex flex-col lg:flex-row">
              <div className="flex-1 flex flex-col p-4 sm:p-6 lg:border-r border-slate-800/50 min-h-[500px] lg:min-h-0 min-w-0">
                
                <div className="flex justify-between items-end mb-4 shrink-0">
                   <div>
                     <h3 className={`text-xl sm:text-2xl font-black ${themeClasses.textHead} uppercase tracking-widest leading-none truncate max-w-[150px] sm:max-w-none`}>{quickData?.ticker}</h3>
                     <p className={`text-[10px] sm:text-xs font-bold ${themeClasses.textMuted} uppercase tracking-widest mt-1 truncate max-w-[150px] sm:max-w-none`}>{quickData?.name}</p>
                   </div>
                   
                   <div className="flex flex-wrap justify-end gap-1.5 sm:gap-2">
                    {TIMEFRAMES.map(tf => (
                      <button 
                        key={tf} 
                        onClick={() => handleTimeframeChange(tf)}
                        className={`px-2 sm:px-3 py-1.5 rounded text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-colors ${diveTimeframe === tf ? 'bg-indigo-600 text-white' : `${themeClasses.input} ${themeClasses.textMuted} hover:text-indigo-400`}`}
                      >
                        {tf}
                      </button>
                    ))}
                  </div>
                </div>

                {diveLoading ? ( 
                  <div className="flex-1 flex items-center justify-center"><Loader2 className="animate-spin text-indigo-500" size={48} /></div>
                ) : diveError ? (
                  <div className="flex-1 flex items-center justify-center flex-col gap-4">
                    <AlertCircle className="text-red-500" size={32} />
                    <p className="text-red-400 font-bold uppercase text-xs">Data Error: {diveError}</p>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col gap-2 overflow-y-auto custom-scrollbar pr-1 sm:pr-2">
                    
                    {(activeOverlays.length > 0 || activeOscillators.length === 0) && (
                      <div className={`flex-[2] min-h-[250px] sm:min-h-[300px] ${themeClasses.input} rounded-2xl border ${themeClasses.border} p-2 sm:p-4 flex flex-col`}>
                        <h4 className="text-[9px] font-black text-indigo-500 uppercase tracking-widest mb-2 px-2">Price Action & Overlays</h4>
                        <div className="flex-1">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={history} syncId="diveSync" margin={{ top: 5, right: 0, left: -25, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" opacity={0.5} />
                              
                              <XAxis dataKey="date" stroke="#94a3b8" fontSize={9} minTickGap={30} axisLine={false} tickLine={false} />
                              <YAxis domain={['auto', 'auto']} stroke="#94a3b8" fontSize={9} tickFormatter={v => `$${v.toFixed(0)}`} axisLine={false} tickLine={false} width={50} />
                              
                              <Tooltip contentStyle={{backgroundColor: '#000', border: '1px solid #334155', borderRadius: '8px', fontSize: '11px'}}/>
                              
                              {selectedMetrics.includes('Bollinger Bands') && <Area type="monotone" dataKey="Upper_Band" stroke="none" fill="#3b82f6" fillOpacity={0.1} />}
                              {selectedMetrics.includes('Bollinger Bands') && <Area type="monotone" dataKey="Lower_Band" stroke="none" fill={themeClasses.bgCard === 'bg-white' ? '#fff' : '#000'} fillOpacity={1} />}
                              {selectedMetrics.includes('Bollinger Bands') && <Line type="monotone" dataKey="Upper_Band" stroke="#3b82f6" strokeWidth={1} dot={false} opacity={0.5}/>}
                              {selectedMetrics.includes('Bollinger Bands') && <Line type="monotone" dataKey="Lower_Band" stroke="#3b82f6" strokeWidth={1} dot={false} opacity={0.5}/>}
                              
                              {(selectedMetrics.includes('Price') || activeOverlays.length === 0) && <Line type="monotone" dataKey="close" name="Price" stroke="#10b981" strokeWidth={2} dot={false} />}
                              {selectedMetrics.includes('SMA 20') && <Line type="monotone" dataKey="SMA_20" stroke="#f59e0b" strokeWidth={1.5} dot={false} />}
                              {selectedMetrics.includes('SMA 50') && <Line type="monotone" dataKey="SMA_50" stroke="#6366f1" strokeWidth={1.5} dot={false} />}
                              {selectedMetrics.includes('SMA 200') && <Line type="monotone" dataKey="SMA_200" stroke="#ec4899" strokeWidth={2} strokeDasharray="5 5" dot={false} />}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {selectedMetrics.includes('Volume') && (
                      <div className={`flex-1 min-h-[100px] sm:min-h-[120px] ${themeClasses.input} rounded-2xl border ${themeClasses.border} p-2 sm:p-4 flex flex-col shrink-0`}>
                        <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 px-2">Trading Volume</h4>
                        <div className="flex-1">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={history} syncId="diveSync" margin={{ top: 0, right: 0, left: -25, bottom: 0 }}>
                              <XAxis dataKey="date" hide={true} />
                              <YAxis domain={['auto', 'auto']} stroke="#94a3b8" fontSize={9} tickFormatter={v => `${(v/1000000).toFixed(0)}M`} axisLine={false} tickLine={false} width={50} />
                              <Tooltip contentStyle={{backgroundColor: '#000', border: '1px solid #334155', borderRadius: '8px', fontSize: '11px'}}/>
                              <Bar dataKey="volume" fill="#475569" opacity={0.5} />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {selectedMetrics.includes('RSI') && (
                      <div className={`flex-1 min-h-[100px] sm:min-h-[120px] ${themeClasses.input} rounded-2xl border ${themeClasses.border} p-2 sm:p-4 flex flex-col shrink-0`}>
                        <h4 className="text-[9px] font-black text-pink-500 uppercase tracking-widest mb-1 px-2">RSI (14)</h4>
                        <div className="flex-1">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={history} syncId="diveSync" margin={{ top: 0, right: 0, left: -25, bottom: 0 }}>
                                <XAxis dataKey="date" hide={true} />
                                <YAxis domain={[0, 100]} stroke="#94a3b8" fontSize={9} axisLine={false} tickLine={false} width={50} ticks={[30, 70]}/>
                                <Tooltip contentStyle={{backgroundColor: '#000', border: '1px solid #334155', borderRadius: '8px', fontSize: '11px'}}/>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" opacity={0.3} horizontalPoints={[30, 70]} />
                                <Line type="monotone" dataKey="RSI" stroke="#ec4899" strokeWidth={1.5} dot={false} />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {selectedMetrics.includes('MACD') && (
                      <div className={`flex-1 min-h-[100px] sm:min-h-[120px] ${themeClasses.input} rounded-2xl border ${themeClasses.border} p-2 sm:p-4 flex flex-col shrink-0`}>
                        <h4 className="text-[9px] font-black text-purple-500 uppercase tracking-widest mb-1 px-2">MACD (12, 26)</h4>
                        <div className="flex-1">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={history} syncId="diveSync" margin={{ top: 0, right: 0, left: -25, bottom: 0 }}>
                                <XAxis dataKey="date" hide={true} />
                                <YAxis domain={['auto', 'auto']} stroke="#94a3b8" fontSize={9} axisLine={false} tickLine={false} width={50} />
                                <Tooltip contentStyle={{backgroundColor: '#000', border: '1px solid #334155', borderRadius: '8px', fontSize: '11px'}}/>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" opacity={0.3} horizontalPoints={[0]} />
                                <Line type="monotone" dataKey="MACD" stroke="#8b5cf6" strokeWidth={1.5} dot={false} />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                  </div>
                )}
                
                <div className="flex flex-wrap gap-2 mt-4 shrink-0 border-t border-slate-800/50 pt-4">
                  <div className="w-full flex justify-between items-center mb-1">
                     <span className={`text-[10px] font-black text-indigo-500 uppercase tracking-widest`}>Technical Indicators</span>
                     <span className={`text-[10px] font-black ${themeClasses.textMuted}`}>{selectedMetrics.length} / 4 Active</span>
                  </div>
                  {ALL_METRICS.map(m => {
                    const isActive = selectedMetrics.includes(m.id);
                    const isMaxed = selectedMetrics.length >= 4 && !isActive;
                    return (
                      <button 
                        key={m.id} 
                        disabled={isMaxed}
                        onClick={() => toggleMetric(m.id)}
                        className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-full border transition-all text-[9px] sm:text-[10px] font-bold uppercase tracking-widest ${isActive ? 'bg-indigo-500/10 border-indigo-500 text-indigo-400' : isMaxed ? `opacity-30 border-transparent ${themeClasses.bgCard} ${themeClasses.textMuted} cursor-not-allowed` : `border-slate-700/50 ${themeClasses.bgCard} ${themeClasses.textMuted} hover:border-slate-500`}`}
                      >
                        <div style={{color: isActive ? m.color : '#64748b'}}>
                          {isActive ? <CheckSquare size={12}/> : <Square size={12}/>}
                        </div>
                        {m.id}
                      </button>
                    )
                  })}
                </div>

                <div className={`mt-3 p-3 sm:p-4 border border-indigo-500/20 bg-indigo-500/5 rounded-2xl shrink-0`}>
                  <h4 className="text-[9px] sm:text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-2 sm:mb-3 flex items-center gap-2"><Activity size={14}/> Active Metric Analysis</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                    {selectedMetrics.length === 0 ? (
                       <p className={`text-[10px] ${themeClasses.textMuted}`}>No metrics selected.</p>
                    ) : (
                      selectedMetrics.map(id => {
                        const m = ALL_METRICS.find(x => x.id === id);
                        return (
                          <div key={id} className="flex gap-2">
                             <span className="w-2 h-2 rounded-full mt-1 shrink-0" style={{backgroundColor: m.color}}></span>
                             <div>
                               <span className={`text-[10px] font-bold ${themeClasses.textHead} block leading-none mb-0.5`}>{m.id}</span>
                               <span className={`text-[9px] ${themeClasses.textMuted} leading-tight`}>{m.desc}</span>
                             </div>
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>

              </div>
              
              <div className="w-full lg:w-[350px] p-4 sm:p-6 flex flex-col gap-4 sm:gap-6 bg-black/20 shrink-0 border-t lg:border-t-0 border-slate-800/50">
                
                <div className={`p-4 sm:p-5 rounded-2xl border ${themeClasses.border} ${themeClasses.bgCard} flex flex-col lg:max-h-[35%] shrink-0 shadow-lg`}>
                  <p className={`text-[10px] font-black text-indigo-500 uppercase mb-2 sm:mb-3 tracking-widest flex items-center gap-2 shrink-0`}><Info size={14}/> Asset Profile</p>
                  <div className="overflow-y-auto custom-scrollbar pr-2 flex-1">
                    <p className={`text-[10px] sm:text-[11px] ${themeClasses.textMain} leading-relaxed font-medium`}>
                      {fullDetails?.description || "Description packet processing..."}
                    </p>
                  </div>
                  
                  <div className={`mt-4 pt-4 border-t ${themeClasses.border}`}>
                      <input type="file" id="prospectus-upload" className="hidden" accept="application/pdf" onChange={handleProspectusUpload} />
                      
                      {prospectusSummary ? (
                         <div className="flex gap-2">
                             <button onClick={() => setShowProspectusModal(true)} className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-colors bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500 hover:text-white`}>
                               <FileText size={14}/> View AI Summary
                             </button>
                             <button onClick={() => setProspectusSummary(null)} className={`p-2 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-colors bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white`} title="Clear Summary">
                               <Trash2 size={14}/>
                             </button>
                         </div>
                      ) : (
                         <label htmlFor="prospectus-upload" className={`w-full py-2 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 cursor-pointer transition-colors ${isUploadingProspectus ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-500/10 text-indigo-500 hover:bg-indigo-500 hover:text-white'}`}>
                           {isUploadingProspectus ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
                           {isUploadingProspectus ? 'Analyzing Document...' : 'Upload Prospectus / 10-K'}
                         </label>
                      )}
                  </div>
                </div>

                <div className={`p-4 sm:p-5 rounded-2xl border ${themeClasses.border} ${themeClasses.bgCard} flex-1 flex flex-col shadow-lg overflow-hidden min-h-[300px]`}>
                  <h4 className={`text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-3 sm:mb-4 flex items-center gap-2 shrink-0`}><BarChart3 size={14}/> Fundamental Data</h4>
                  <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-2 sm:space-y-3">
                    {fullDetails?.fundamentals ? (
                      Object.entries(fullDetails.fundamentals).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center border-b border-slate-700/50 pb-2 hover:bg-slate-800/30 transition-colors px-1 rounded group">
                           <span 
                             className={`text-[9px] sm:text-[10px] uppercase font-bold ${themeClasses.textMuted} cursor-help border-b border-dotted border-slate-600 transition-colors group-hover:text-emerald-400 group-hover:border-emerald-500/50`}
                             title={FUNDAMENTAL_TOOLTIPS[key]}
                           >
                             {key} <span className="text-[8px] opacity-50 hidden sm:inline">?</span>
                           </span>
                           <span className={`text-[10px] sm:text-xs font-mono font-bold ${themeClasses.textHead}`}>{value}</span>
                        </div>
                      ))
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-center gap-2 opacity-50 pb-4">
                        <Loader2 className="animate-spin text-emerald-500" size={24}/>
                        <p className={`text-[10px] font-bold ${themeClasses.textMuted} uppercase`}>Awaiting Fundamentals...</p>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      )}
      
      {showProspectusModal && (
        <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4 sm:p-6">
          <div className={`${themeClasses.bgCard} border ${themeClasses.border} w-full max-w-3xl h-[80vh] sm:h-[70vh] rounded-[2rem] sm:rounded-[3rem] overflow-hidden flex flex-col shadow-2xl`}>
            <div className={`p-6 sm:p-8 border-b ${themeClasses.border} flex justify-between items-center shrink-0`}>
              <h2 className={`text-xl sm:text-2xl font-black ${themeClasses.textHead} uppercase flex items-center gap-3`}><FileText size={24} className="text-emerald-500"/> AI Prospectus Analysis</h2>
              <button onClick={() => setShowProspectusModal(false)} className={`${themeClasses.input} p-2 sm:p-2.5 rounded-xl hover:text-red-500`}><X size={20}/></button>
            </div>
            <div className={`p-6 sm:p-8 flex-1 overflow-y-auto custom-scrollbar ${themeClasses.input}`}>
               <div className="max-w-2xl mx-auto space-y-4">
                  <div className={`text-sm sm:text-base leading-relaxed text-emerald-400`}>
                     {formatMarkdownText(prospectusSummary)}
                  </div>
               </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}